// client/src/context/QuoteContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';

const QuoteContext = createContext();

const QUOTE_CART_KEY = 'makah_quote_cart_items';

export const useQuote = () => {
  return useContext(QuoteContext);
};

export const QuoteProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [productForQuote, setProductForQuote] = useState(null);
  const [quoteItems, setQuoteItems] = useState(() => {
    try {
      const saved = localStorage.getItem(QUOTE_CART_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (err) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(QUOTE_CART_KEY, JSON.stringify(quoteItems));
    } catch (err) {
      console.error('Could not persist quote items:', err);
    }
  }, [quoteItems]);

  const openModal = (product = null) => {
    setProductForQuote(product);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setProductForQuote(null);
  };

  const addToQuoteList = (product, quantity = 1) => {
    if (!product) return;
    setQuoteItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === product.id || item.name === product.name
      );
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + Number(quantity || 1),
        };
        return updated;
      }
      return [
        ...prev,
        {
          id: product.id || `custom-${Date.now()}`,
          name: product.name,
          category: product.category || 'Medical Supply',
          imageUrl: product.imageUrl || null,
          quantity: Number(quantity || 1),
        },
      ];
    });
  };

  const updateQuoteItemQuantity = (idOrName, quantity) => {
    const qty = Math.max(1, Number(quantity) || 1);
    setQuoteItems((prev) =>
      prev.map((item) =>
        item.id === idOrName || item.name === idOrName ? { ...item, quantity: qty } : item
      )
    );
  };

  const removeFromQuoteList = (idOrName) => {
    setQuoteItems((prev) =>
      prev.filter((item) => item.id !== idOrName && item.name !== idOrName)
    );
  };

  const clearQuoteList = () => {
    setQuoteItems([]);
  };

  const value = {
    isModalOpen,
    productForQuote,
    quoteItems,
    openModal,
    closeModal,
    addToQuoteList,
    updateQuoteItemQuantity,
    removeFromQuoteList,
    clearQuoteList,
  };

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
};
